import { Check } from "lucide-react";

export interface Step {
  number: number;
  title: string;
  shortTitle?: string;
  subtitle?: string;
  icon: any;
  completed: boolean;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepNumber: number) => void;
  getStepProgress: () => number;
  size?: "default" | "small";
  clickable?: boolean;
}

export const StepIndicator = ({
  steps,
  currentStep,
  onStepClick,
  getStepProgress,
  size = "default",
  clickable = false,
}: StepIndicatorProps) => {
  const isSmall = size === "small";

  return (
    <div
      className={`relative ${isSmall ? "" : "mt-1"} overflow-hidden sm:overflow-visible -mx-2 px-2`}
      data-sentry-unmask
    >
      {/* Progress line is hidden on small screens to avoid layout issues */}
      {!isSmall && (
        <>
          <div className="absolute top-4 sm:top-5 left-0 right-0 h-px bg-gray-200 hidden sm:block" />
          <div
            className="absolute top-4 sm:top-5 left-0 h-px bg-gray-900 transition-all duration-500 ease-out hidden sm:block"
            style={{ width: `${getStepProgress()}%` }}
          />
        </>
      )}
      <div
        className={`flex ${isSmall ? "justify-start gap-3 w-fit" : "justify-between gap-1.5 sm:gap-0 w-full"} relative`}
      >
        {steps.map((step) => {
          const Icon = step.icon as any;
          const isActive = currentStep === step.number;
          const isCompleted = step.completed;
          const isClickable = step.number <= currentStep;
          return (
            <div
              key={step.number}
              className={`flex flex-col items-center transition-all duration-200 ${isSmall ? "flex-initial" : "flex-1 sm:flex-initial"} min-w-0 ${clickable && isClickable ? "cursor-pointer hover:scale-105" : ""}`}
              onClick={() => clickable && isClickable && onStepClick?.(step.number)}
            >
              <div
                className={`
                  flex items-center justify-center transition-all duration-200 border-2
                  ${
                    isSmall
                      ? isActive
                        ? "h-6 px-3 gap-2 rounded-full bg-gray-100 border-gray-200 text-gray-700"
                        : `w-6 h-6 rounded-full ${isCompleted ? "bg-gray-900 border-gray-900 text-white" : "bg-white border-gray-200 text-gray-400"}`
                      : `w-7 h-7 sm:w-8 sm:h-8 rounded-full ${isCompleted ? "bg-gray-900 border-gray-900 text-white" : isActive ? "bg-white border-gray-900 text-gray-900 shadow-sm" : "bg-white border-gray-200 text-gray-400"}`
                  }
                `}
              >
                {isCompleted ? (
                  <Check className={`${isSmall ? "w-3 h-3" : "w-3 h-3 sm:w-3.5 sm:h-3.5"}`} />
                ) : (
                  <Icon className={`${isSmall ? "w-3 h-3" : "w-3 h-3 sm:w-3.5 sm:h-3.5"}`} />
                )}

                {isSmall && isActive && (
                  <span className="step-title text-[10px] font-bold whitespace-nowrap leading-none">
                    {step.shortTitle ?? step.title}
                  </span>
                )}
              </div>

              {/* Text for Normal Mode */}
              {!isSmall && (
                <div className="mt-1 sm:mt-1.5 text-center leading-none">
                  <div
                    className={`step-title text-[10px] sm:text-[11px] font-medium transition-colors duration-200 ${isActive || isCompleted ? "text-gray-900" : "text-gray-500"}`}
                  >
                    <span className="sm:hidden">
                      {step.shortTitle ?? step.title}
                    </span>
                    <span className="hidden sm:inline">{step.title}</span>
                  </div>
                  <div className="step-title text-[10px] text-gray-400 mt-0.5 hidden sm:block">
                    {step.subtitle}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
